import { FC, useEffect, useMemo, useState, useRef } from "react";
import classNames from "classnames";
import { isMonday as isMondayFns } from 'date-fns';
import {
  LabelList,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
  Tooltip,
  Area,
} from "recharts";
import OutsideClickHandler from "../OutsideClickHandler/OutsideClickHandler";

import { IChartProps, INormalizedChartDate, ChartSeriesKey, TooltipState } from "./types";
import CustomDot from "./components/CustomDot/CustomDot";
import { normalizeData } from "./utils/normalizeData";
import { EColor } from "../../constants/colors";
import CustomTooltip from "./components/CustomTooltip/CustomTooltip";
import { isOnlyThisEnabled } from "./utils/isOnlyThisEnabled";
import { ChartGradients } from "./components/ChartGradients";
import { createBarLabelRenderer } from "./components/createBarLabelRenderer";
import { createBarShapeRenderer } from "./components/createBarShapeRenderer";

import "../../assets/css/library/recharts.css";
import styles from "./Chart.module.css";



const Chart: FC<IChartProps> = ({ data, rangeValues, onDotClick, visibleSeries }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [activeDotPayload, setActiveDotPayload] = useState<INormalizedChartDate | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState>(null);
  const normalizedData = useMemo(() => normalizeData(data, rangeValues || {}), [data, rangeValues])

  const mondayDates = useMemo(() =>
    normalizedData
      .filter(point => {
        if (!point.created_at) return false;
        const parsedDate = new Date(point.created_at);
        return isMondayFns(parsedDate);
      })
      .map(point => point.date),
    [normalizedData]
  );

  const handleOutsideClick = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setTooltip(null);
    setActiveDotPayload(null);
  };

  const handleDotClick = (created_at: string) => {
    onDotClick(created_at);
  };

  const handleTooltipShow = (type: 'line' | 'bar') => (x: number, y: number, payload: INormalizedChartDate) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    
    setTooltip({
      type,
      x,
      y,
      payload,
    });
    setActiveDotPayload(payload);
  };

  const handleTooltipHide = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setTooltip(null);
      setActiveDotPayload(null);
      closeTimeoutRef.current = null;
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);

  const renderBarLabel = useMemo(
    () => createBarLabelRenderer(visibleSeries),
    [visibleSeries]
  );

  const renderBarShape = useMemo(
    () => createBarShapeRenderer(chartContainerRef, handleTooltipShow('bar'), handleTooltipHide),
    [handleTooltipShow, handleTooltipHide]
  );

  const handleBarClick = (payload: INormalizedChartDate) => {
    if (payload.created_at) {
      onDotClick(payload.created_at);
    }
  };

  return (
    <OutsideClickHandler
      onOutsideClick={handleOutsideClick}
      disabled={!tooltip}
    >
      <div className={styles["graph"]} ref={chartContainerRef} style={{ position: 'relative' }}>
        <div className={styles["graph__wrapper"]}>
          <div className={classNames(styles["graph__label"], styles["is-scores"])}>
            <p>scores</p>
          </div>
          <ResponsiveContainer width="100%" height={500}>
            <ComposedChart
              data={normalizedData}
              margin={{ top: 40 }}
            >
              <Tooltip content={() => null} />
              <CartesianGrid strokeDasharray="2 2" />
              {mondayDates.map((date) => (
                <ReferenceLine
                  yAxisId="left"
                  key={`monday-${date}`}
                  x={date}
                  stroke={EColor.Gray2}
                />
              ))}
              <XAxis
                dataKey="date"
                tickLine={false}
                allowDataOverflow
                axisLine={false}
                minTickGap={-2}
                padding={{ left: 9, right: 9 }}
                tickFormatter={(value, index) => (index % 2 !== 0 ? value : "")}
              />
              <YAxis
                yAxisId="left"
                tickCount={11}
                tickMargin={10}
                axisLine={{ strokeWidth: 0 }}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={(value) => (value % 20 === 0 ? value : "")}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickCount={6}
                tickMargin={10}
                axisLine={{ strokeWidth: 0 }}
                tickLine={false}
                domain={[1, 6]}
                tickFormatter={(value) => `#${7 - value}`}
              />
              <Bar
                yAxisId="right"
                fill={`url(#bar-gradient)`}
                dataKey={visibleSeries[ChartSeriesKey.Ranking] ? "rankingBar" : ""}
                shape={isOnlyThisEnabled(visibleSeries, ChartSeriesKey.Ranking) ? renderBarShape : undefined}
                onClick={handleBarClick}
              >
                {visibleSeries[ChartSeriesKey.Ranking] && <LabelList dataKey="rankingBar" content={renderBarLabel} />}
              </Bar>
              {isOnlyThisEnabled(visibleSeries, ChartSeriesKey.Visibility) && (
                <Area 
                  type="monotone" 
                  yAxisId="left"  
                  dataKey="visibilityRange" 
                  stroke="none" 
                  fill={EColor.Green4}
                  opacity={0.3}
                  connectNulls 
                  dot={false} 
                  activeDot={false} 
                  filter="url(#area-filter-green)"
                />
              )}
              {isOnlyThisEnabled(visibleSeries, ChartSeriesKey.Sentiment) && (
                <Area 
                  type="monotone" 
                  yAxisId="left" 
                  dataKey="sentimentRange" 
                  stroke="none" 
                  fill={EColor.Blue3}
                  opacity={0.3}
                  connectNulls 
                  dot={false} 
                  activeDot={false}
                  filter="url(#area-filter-blue)"
                />
              )}
              <Line
                hide={!visibleSeries[ChartSeriesKey.Visibility]}
                type="monotone"
                yAxisId="left"
                connectNulls
                dataKey="visibility"
                strokeWidth={8}
                stroke={EColor.Green}
                isAnimationActive={false}
                dot={<CustomDot 
                  fillColor={EColor.Green2} 
                  onClickDot={handleDotClick} 
                  onTooltipShow={handleTooltipShow('line')} 
                  onTooltipHide={handleTooltipHide} 
                  activeDotPayload={activeDotPayload} 
                />}
                activeDot={<CustomDot 
                  isHover 
                  fillColor={EColor.Green2} 
                  onClickDot={handleDotClick} 
                  onTooltipShow={handleTooltipShow('line')} 
                  onTooltipHide={handleTooltipHide} 
                  activeDotPayload={activeDotPayload} 
                />}
              />
              <Line
                hide={!visibleSeries[ChartSeriesKey.Sentiment]}
                type="monotone"
                yAxisId="left"
                connectNulls
                dataKey="sentiment"
                strokeWidth={8}
                stroke={EColor.Blue}
                isAnimationActive={false}
                dot={<CustomDot 
                  fillColor={EColor.Blue2} 
                  onClickDot={handleDotClick} 
                  onTooltipShow={handleTooltipShow('line')} 
                  onTooltipHide={handleTooltipHide} 
                  activeDotPayload={activeDotPayload} 
                />}
                activeDot={<CustomDot 
                  isHover 
                  fillColor={EColor.Blue2} 
                  onClickDot={handleDotClick} 
                  onTooltipShow={handleTooltipShow('line')} 
                  onTooltipHide={handleTooltipHide} 
                  activeDotPayload={activeDotPayload} 
                />}
              />
              <defs><ChartGradients /></defs>
            </ComposedChart>
          </ResponsiveContainer>
          <div className={classNames(styles["graph__label"], styles["is-ranking"])}>
            <p>ranking</p>
          </div>
        </div>
        {tooltip && <CustomTooltip
          onClickTooltip={handleDotClick} 
          tooltip={tooltip} 
          tooltipRef={tooltipRef} 
          closeTimeoutRef={closeTimeoutRef} 
          setTooltip={setTooltip} 
          setActiveDotPayload={setActiveDotPayload}
          visibleSeries={visibleSeries}
          />}
      </div>
    </OutsideClickHandler>
  );
};

export default Chart;